import { supabase } from './supabase';
import { isCdnUploadEnabled, toMediaUrl } from './mediaUrl';

export type UploadPurpose = 'gallery' | 'v2v_base' | 'frame' | 'recolor' | 'audio';

export type PresignedUpload = {
  upload_url: string;
  key: string;
  public_url: string;
  content_type: string;
};

/** Ask Edge for a short-lived S3 PUT URL (P7). */
export async function requestUploadUrl(args: {
  organizationId: string;
  contentType: string;
  filename?: string;
  purpose?: UploadPurpose;
}): Promise<PresignedUpload> {
  const { data, error } = await supabase.functions.invoke('get-upload-url', {
    body: {
      organization_id: args.organizationId,
      content_type: args.contentType,
      filename: args.filename,
      purpose: args.purpose || 'gallery',
    },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  if (!data?.upload_url || !data?.key || !data?.public_url) {
    throw new Error('get-upload-url não retornou upload_url/key/public_url');
  }
  return {
    upload_url: data.upload_url as string,
    key: data.key as string,
    public_url: toMediaUrl(data.public_url as string),
    content_type: (data.content_type as string) || args.contentType,
  };
}

/** PUT bytes to the presigned URL. */
export async function putToPresignedUrl(
  uploadUrl: string,
  body: Blob,
  contentType: string,
): Promise<void> {
  const resp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Upload S3 falhou (HTTP ${resp.status}): ${text.slice(0, 200)}`);
  }
}

/**
 * Upload file/blob to media CDN (S3 + CloudFront); returns public CDN URL + key.
 * Throws if CDN is disabled or Edge/S3 fails — no Supabase Storage fallback.
 */
export async function uploadViaCdn(args: {
  organizationId: string;
  file: Blob;
  contentType?: string;
  filename?: string;
  purpose?: UploadPurpose;
}): Promise<{ publicUrl: string; key: string }> {
  if (!isCdnUploadEnabled()) {
    throw new Error('CDN upload desabilitado (REACT_APP_MEDIA_CDN_URL)');
  }
  const contentType = args.contentType || args.file.type || 'application/octet-stream';
  const signed = await requestUploadUrl({
    organizationId: args.organizationId,
    contentType,
    filename: args.filename,
    purpose: args.purpose,
  });
  await putToPresignedUrl(signed.upload_url, args.file, signed.content_type);
  return { publicUrl: signed.public_url, key: signed.key };
}
